import { useCallback, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type Presence = { renderedId: string | null; closing: boolean };

/**
 * Keeps a pane mounted long enough to animate itself out.
 *
 * React unmounts the moment its condition goes false, which is why an exit
 * animation needs somewhere to live. This lags the real selection: it adopts a
 * new id immediately, but on close it holds the old one and reports `closing`
 * until the caller says the animation has finished.
 *
 * The state is adjusted during render rather than in an effect. That is React's
 * documented way to derive state from a changing prop — the re-render happens
 * before anything is committed, so the pane never paints a frame in the wrong
 * phase, and there is no cascading render for the linter to object to.
 *
 * Reduced motion is handled here rather than with a `motion-safe:` class,
 * deliberately. If the class were skipped the animation would never run, no
 * animationend would fire, and the pane would stay mounted forever — it would
 * simply never close. So instead: no motion, no exit phase, unmount now.
 */
export const usePanePresence = (selectedId: string | null) => {
  const [presence, setPresence] = useState<Presence>({
    renderedId: selectedId,
    closing: false,
  });

  if (selectedId) {
    if (presence.renderedId !== selectedId || presence.closing) {
      setPresence({ renderedId: selectedId, closing: false });
    }
  } else if (presence.renderedId !== null && !presence.closing) {
    setPresence({
      renderedId: prefersReducedMotion() ? null : presence.renderedId,
      closing: !prefersReducedMotion(),
    });
  }

  const onExited = useCallback(() => {
    // Guard against a reopen that landed while the exit was still running.
    setPresence((current) =>
      current.closing ? { renderedId: null, closing: false } : current,
    );
  }, []);

  return { ...presence, onExited };
};
